import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {markChatUseCaseUsed} from 'llm/chat-library/use-cases/chatUseCaseUsage';

/** Which entry point the use case was run from, so the run metric can be split by surface. */
export type ChatUseCaseRunSource = 'use_cases_tab' | 'progressive_disclosure' | 'onboarding' | 'url';

interface ChatUseCaseEventProperties {
  chatUseCaseId: string;
  categoryId: string;
  source: ChatUseCaseRunSource;
  campaign: string | null;
}

/**
 * Record a use case the user actually ran.
 *
 * The analytics event and the stored history describe the same run, so every entry point records
 * both through here: no surface can report a run to one and not the other, which keeps the metric
 * and the "already run" badge describing the same set of runs.
 *
 * Call this only once the chat exists. An attempt that failed or was declined is not a run, and
 * counting it would both skew the metric and sort a prompt the user never saw as already tried.
 *
 * `campaign` is the optional marketing campaign that opened this run (from `utm_campaign` on a
 * catalog URL). In-product surfaces omit it.
 */
export default function recordChatUseCaseRun(
  chatUseCaseId: string,
  {categoryId, source, campaign}: {categoryId: string; source: ChatUseCaseRunSource; campaign: string | null}
): void {
  const eventProperties: ChatUseCaseEventProperties = {chatUseCaseId, categoryId, source, campaign};
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.RAN_CHAT_USE_CASE, eventProperties);
  markChatUseCaseUsed(chatUseCaseId);
}

/**
 * Record a use case whose prompt was put in the composer for the user to send themselves.
 *
 * Only the event is written, never the stored history: the user may never send the prompt, and a
 * use case they did not run must keep reading as untried wherever the catalog is listed.
 *
 * `campaign` is the optional marketing campaign on the landing URL, so prefill can be joined to
 * the later run of the same campaign.
 */
export function recordChatUseCasePrefill(
  chatUseCaseId: string,
  {categoryId, campaign}: {categoryId: string; campaign: string | null}
): void {
  const eventProperties: ChatUseCaseEventProperties = {
    chatUseCaseId,
    categoryId,
    source: 'url',
    campaign,
  };
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.PREFILLED_CHAT_USE_CASE, eventProperties);
}
